import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/auth';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import LoadingSpinner from '../ui/LoadingSpinner';

interface StatisticsTabProps {
  clientId: string;
}

type Period = 'month' | '3months' | '6months' | 'all';

interface GlobalStats {
  total_rdv: number;
  total_calls: number;
  total_conversations: number;
  conversion_rate: number;
}

interface EvolutionData {
  month: string;
  rdv_count: number;
}

const StatisticsTab: React.FC<StatisticsTabProps> = ({ clientId }) => {
  const [period, setPeriod] = useState<Period>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [globalStats, setGlobalStats] = useState<GlobalStats>({
    total_rdv: 0,
    total_calls: 0,
    total_conversations: 0,
    conversion_rate: 0,
  });
  const [evolutionData, setEvolutionData] = useState<EvolutionData[]>([]);

  useEffect(() => {
    fetchData();
  }, [clientId, period]);

  const getDateFilter = () => {
    const now = new Date();
    switch (period) {
      case 'month':
        const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return oneMonthAgo.toISOString();
      case '3months':
        const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        return threeMonthsAgo.toISOString();
      case '6months':
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        return sixMonthsAgo.toISOString();
      case 'all':
      default:
        return null;
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchGlobalStats(), fetchEvolutionData()]);
    setIsLoading(false);
  };

  const fetchGlobalStats = async () => {
    try {
      const dateFilter = getDateFilter();

      let prospectingQuery = supabase
        .from('prospecting_days')
        .select('calls_count, conversations_count, date')
        .eq('client_id', clientId);

      if (dateFilter) {
        prospectingQuery = prospectingQuery.gte('date', dateFilter);
      }

      const { data: prospectingData, error: prospectingError } = await prospectingQuery;
      if (prospectingError) throw prospectingError;

      let appointmentsQuery = supabase
        .from('appointments')
        .select('id, date')
        .eq('client_id', clientId)
        .neq('status', 'cancelled');

      if (dateFilter) {
        appointmentsQuery = appointmentsQuery.gte('date', dateFilter);
      }

      const { data: appointmentsData, error: appointmentsError } = await appointmentsQuery;
      if (appointmentsError) throw appointmentsError;

      const total_calls = (prospectingData || []).reduce((sum, day) => sum + (day.calls_count || 0), 0);
      const total_conversations = (prospectingData || []).reduce((sum, day) => sum + (day.conversations_count || 0), 0);
      const total_rdv = (appointmentsData || []).length;

      const conversion_rate = total_calls > 0 ? (total_rdv / total_calls) * 100 : 0;

      setGlobalStats({
        total_rdv,
        total_calls,
        total_conversations,
        conversion_rate: Math.round(conversion_rate * 10) / 10,
      });
    } catch (error) {
      console.error('Error fetching global stats:', error);
    }
  };

  const fetchEvolutionData = async () => {
    try {
      const dateFilter = getDateFilter();
      let query = supabase
        .from('appointments')
        .select('date')
        .eq('client_id', clientId)
        .neq('status', 'cancelled');

      if (dateFilter) {
        query = query.gte('date', dateFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const grouped = (data || []).reduce((acc, item) => {
        const date = new Date(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });

        if (!acc[monthKey]) {
          acc[monthKey] = { month: monthLabel, rdv_count: 0 };
        }
        acc[monthKey].rdv_count++;
        return acc;
      }, {} as Record<string, EvolutionData>);

      const evolutionArray = Object.values(grouped).sort((a, b) => {
        const [aYear, aMonth] = a.month.split(' ');
        const [bYear, bMonth] = b.month.split(' ');
        return new Date(`${aMonth} 1, ${aYear}`).getTime() - new Date(`${bMonth} 1, ${bYear}`).getTime();
      });

      setEvolutionData(evolutionArray);
    } catch (error) {
      console.error('Error fetching evolution data:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Statistiques</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPeriod('month')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              period === 'month'
                ? 'bg-[#E91E8C] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Ce mois
          </button>
          <button
            onClick={() => setPeriod('3months')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              period === '3months'
                ? 'bg-[#E91E8C] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            3 mois
          </button>
          <button
            onClick={() => setPeriod('6months')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              period === '6months'
                ? 'bg-[#E91E8C] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            6 mois
          </button>
          <button
            onClick={() => setPeriod('all')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              period === 'all'
                ? 'bg-[#E91E8C] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Tout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎯</span>
            <span className="text-sm font-medium text-gray-600">Total RDV</span>
          </div>
          <div className="text-3xl font-bold text-[#E91E8C]">{globalStats.total_rdv}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📞</span>
            <span className="text-sm font-medium text-gray-600">Appels passés</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{globalStats.total_calls}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">💬</span>
            <span className="text-sm font-medium text-gray-600">Conversations</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{globalStats.total_conversations}</div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📈</span>
            <span className="text-sm font-medium text-gray-600">Taux conversion</span>
          </div>
          <div className="text-3xl font-bold text-gray-900">{globalStats.conversion_rate}%</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des RDV</h3>
        {evolutionData.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            Pas encore de données
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={evolutionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: 12 }} />
              <YAxis stroke="#6b7280" style={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: 12,
                }}
              />
              <Line type="monotone" dataKey="rdv_count" stroke="#E91E8C" strokeWidth={2} dot={{ fill: '#E91E8C', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

    </div>
  );
};

export default StatisticsTab;
